import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@material-ui/core";

import apiClient from "../../../auth/apiClient";
import MobilePaymentService from "../../../services/MobilePaymentService";
import GenericError from "../GenericError";
import ReceiptPanel from "./ReceiptPanel";

const mobilePaymentService = new MobilePaymentService(apiClient);

/** Maximum time to wait for the receipts request before showing an error. */
export const RECEIPT_FETCH_TIMEOUT_MS = 10000;

/**
 * Races a promise against a timeout. If the supplied promise does not settle
 * within `timeoutMs`, the returned promise rejects with a timeout error so the
 * caller renders the receipt-retrieval error state (Requirement 10.4).
 *
 * @param {Promise} promise The work to race (the receipts request).
 * @param {number} timeoutMs The timeout in milliseconds.
 * @returns {Promise} A promise that settles with `promise` or rejects on timeout.
 */
const withTimeout = (promise, timeoutMs) => {
  let timeoutId;
  const timeout = new Promise((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("Receipt request timed out")),
      timeoutMs
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

/**
 * Container for the Receipt Panels shown on the Ticket Summary Portal.
 *
 * Fetches the set of completed payment-transaction receipts for the ticket via
 * `MobilePaymentService.getTicketReceipts` inside an effect, guarded by a
 * 10-second timeout (Requirement 10.4). While the request is in flight a
 * spinner is shown. On failure or timeout a receipt-retrieval error is shown
 * and no partial content is rendered (Requirements 10.4). When the ticket has
 * no payment transactions the component renders nothing and no error
 * (Requirements 2.2, 10.3). Otherwise it renders exactly one `ReceiptPanel`
 * per receipt (Requirements 2.1, 8.1), preserving the server-provided order,
 * which is authoritative (Requirements 8.2, 8.3).
 *
 * @param {object} props Component props.
 * @param {string} props.ticketId The ticket identifier to load receipts for.
 */
const ReceiptPanelList = ({ ticketId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [receipts, setReceipts] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchReceipts = async () => {
      if (!ticketId) {
        if (isMounted) {
          setReceipts([]);
          setErrorMessage(null);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response = await withTimeout(
          mobilePaymentService.getTicketReceipts(ticketId),
          RECEIPT_FETCH_TIMEOUT_MS
        );
        if (!isMounted) {
          return;
        }
        const fetched = response?.data?.receipts;
        setReceipts(Array.isArray(fetched) ? fetched : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        // On any failure or timeout render the error state and no partial
        // content (Requirement 10.4).
        setReceipts([]);
        setErrorMessage([
          "Unable to retrieve receipts.",
          "Please try re-scanning with your phone.",
        ]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchReceipts();

    return () => {
      isMounted = false;
    };
  }, [ticketId]);

  if (isLoading) {
    return (
      <CircularProgress
        size={60}
        className="receipt-list-loading"
        data-testid="receipt-list-loading"
      />
    );
  }

  if (errorMessage) {
    return (
      <div className="receipt-list-error">
        <GenericError
          message={errorMessage}
          headerMessage="Unable to retrieve receipts."
        />
      </div>
    );
  }

  // Empty set: render nothing and no error (Requirements 2.2, 10.3).
  if (!Array.isArray(receipts) || receipts.length === 0) {
    return null;
  }

  // Preserve server order (authoritative ordering per the BFF).
  return (
    <div className="receipt-panel-list" data-testid="receipt-panel-list">
      {receipts.map((receipt, index) => (
        // Multiple payments against the same ticket share one transaction id
        // (each is a distinct payment event), so the transaction id alone is not
        // unique. Compose it with the array index to keep React keys stable and
        // unique across panels for the same transaction.
        <ReceiptPanel
          key={`${receipt?.transactionId ?? "receipt"}-${index}`}
          receipt={receipt}
        />
      ))}
    </div>
  );
};

ReceiptPanelList.propTypes = {
  ticketId: PropTypes.string,
};

export default ReceiptPanelList;
